import React, { useState, useEffect, useCallback } from "react";
import { Delete as DeleteIcon } from "@mui/icons-material";
import useInvoiceService from '../../hooks/useInvoiceService';
import useUserService from '../../hooks/useUserService';
import { handleApiError } from '../../components/Utils';
import { useSnackbar } from '../../components/SnackbarProvider';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Autocomplete,
    TextField,
    Button,
    Grid,
    IconButton,
    CircularProgress,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";

function InvoiceValidatorsConfig() {
    const { getInvoiceValidators, addInvoiceValidator, removeInvoiceValidator } = useInvoiceService();
    const { getUsers } = useUserService();
    const showSnackbar = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [validators, setValidators] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [userIdToRemove, setUserIdToRemove] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [validatorsResult, usersResult] = await Promise.all([
                getInvoiceValidators(),
                getUsers(),
            ]);

            const adminUsers = (usersResult.data?.items || []).filter(
                (user) => user.role === 'admin' || user.role === 'superadmin'
            );

            setValidators(validatorsResult.data);
            setAdmins(adminUsers);
            setError(null);
        } catch (e) {
            const errorMessage = handleApiError(e, 'Error obteniendo validadores de facturas.');
            setError(errorMessage);
            showSnackbar(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, []);

    const availableAdmins = admins.filter(
        (admin) => !validators.some((validator) => validator.user_id === admin.id)
    );

    const handleAddValidator = async () => {
        if (!selectedAdmin) return;
        let error = null;
        try {
            setSubmitting(true);
            await addInvoiceValidator(selectedAdmin.id);
            showSnackbar('Validador añadido correctamente.');
            setSelectedAdmin(null);
            fetchData();
        } catch (e) {
            error = handleApiError(e, 'Error inesperado añadiendo validador.');
        } finally {
            if (error) showSnackbar(error, 'error');
            setSubmitting(false);
        }
    };

    const handleOpenRemoveConfirm = (userId) => {
        setUserIdToRemove(userId);
        setConfirmOpen(true);
    };

    const handleCloseRemoveConfirm = () => {
        setConfirmOpen(false);
        setUserIdToRemove(null);
    };

    const handleConfirmRemoveValidator = async () => {
        if (!userIdToRemove) return;
        let error = null;
        try {
            setSubmitting(true);
            const response = await removeInvoiceValidator(userIdToRemove);
            const approvedInvoices = response?.data?.approved_invoices || 0;
            showSnackbar(
                approvedInvoices > 0
                    ? `Validador eliminado. ${approvedInvoices} borrador(es) aprobado(s) como factura(s).`
                    : 'Validador eliminado.'
            );
            fetchData();
        } catch (e) {
            error = handleApiError(e, 'Error inesperado eliminando validador.');
        } finally {
            if (error) showSnackbar(error, 'error');
            setSubmitting(false);
            setConfirmOpen(false);
            setUserIdToRemove(null);
        }
    };

    return (
        <div>
            <Typography variant="h5" gutterBottom color="primary">
                Administradores validadores de facturas
            </Typography>

            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            )}

            {!loading && (
                <>
                    <Grid container spacing={1} alignItems="center" sx={{ marginBottom: 3, marginTop: 1 }}>
                        <Grid item xs={12} sm={6} md={4}>
                            <Autocomplete
                                options={availableAdmins}
                                getOptionLabel={(option) => `${option.name || option.email} - ${option.email}`}
                                value={selectedAdmin}
                                onChange={(event, newValue) => setSelectedAdmin(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Administrador"
                                        style={{ maxWidth: '100%' }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddValidator}
                                disabled={!selectedAdmin || submitting}
                            >
                                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Añadir'}
                            </Button>
                        </Grid>
                    </Grid>

                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>Nombre</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>Email</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="h6" gutterBottom>Acciones</Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {validators.length > 0 ? (
                                    validators.map((validator) => (
                                        <TableRow key={validator.user_id}>
                                            <TableCell component="th" scope="row">
                                                {validator.user_name}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {validator.email}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    edge="end"
                                                    color="secondary"
                                                    disabled={submitting}
                                                    onClick={() => handleOpenRemoveConfirm(validator.user_id)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            No hay validadores configurados.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            <Dialog open={confirmOpen} onClose={handleCloseRemoveConfirm}>
                <DialogTitle>Eliminar validador</DialogTitle>
                <DialogContent>
                    <Typography>
                        Al eliminar este validador, todos los borradores que ya cuenten con la validación de todos los validadores restantes se convertirán automáticamente en facturas. ¿Deseas continuar?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseRemoveConfirm} color="primary" disabled={submitting}>
                        Cancelar
                    </Button>
                    <Button onClick={handleConfirmRemoveValidator} color="error" variant="contained" disabled={submitting}>
                        {submitting ? <CircularProgress size={24} color="inherit" /> : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {error && (
                <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                    {error}
                </Typography>
            )}
        </div>
    );
}

export default InvoiceValidatorsConfig;
